import React, { useState, useEffect, useCallback } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { 
  CreditCard, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  Loader2, 
  ExternalLink,
  ArrowUp,
  ArrowDown,
  Crown,
  Zap,
  Sparkles,
  FileText,
  AlertTriangle,
  X,
  RefreshCw,
  Download,
  Clock,
  DollarSign,
  Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { SubscriptionButton } from '../components/SubscriptionButton';
import { getPlanVariant } from '../stripe-config';
import type { PlanId } from '../stripe-config';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { usePauseSubscription } from '../hooks/usePauseSubscription';

interface SubscriptionDetails {
  plan: string | null;
  status: string | null;
  currentPeriodEnd: string | null;
  currentPeriodStart: string | null;
  cancelAtPeriodEnd: boolean;
  amount: number | null;
  currency: string;
  paymentMethod: {
    brand: string | null;
    last4: string | null;
    expMonth: number | null;
    expYear: number | null;
  } | null;
}

interface Invoice {
  id: string;
  number: string;
  amount: number;
  currency: string;
  status: string;
  created: number;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

const PageBilling: React.FC = () => {
  const { user, session } = useAuth();
  const { 
    plan, 
    status, 
    currentPeriodEnd, 
    isLoading, 
    error,
    isTrialEligible,
    openBillingPortal,
    refreshSubscription,
    cancelSubscription
  } = useSubscription();

  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  
  // Hook pour pause/reprise d'abonnement
  const { pauseSubscription, resumeSubscription, loading: pauseLoading } = usePauseSubscription();
  
  // ✅ Fonction helper pour refresh les détails de subscription (évite la duplication)
  const refreshSubscriptionDetails = useCallback(async () => {
    if (!user || !supabase) return;
    
    const { data: customer } = await supabase
      .from('stripe_customers')
      .select('customer_id')
      .eq('user_id', user.id)
      .is('deleted_at', null)
      .maybeSingle();
      
    if (customer) {
      const { data: subscription } = await supabase
        .from('stripe_subscriptions')
        .select('*')
        .eq('customer_id', customer.customer_id)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
        
      if (subscription) {
        // ✅ current_period_end est déjà en TIMESTAMPTZ (ISO string) dans la DB
        setSubscriptionDetails({
          plan: subscription.subscription_plan || null,
          status: subscription.status || null,
          currentPeriodEnd: subscription.current_period_end || null,
          currentPeriodStart: subscription.current_period_start || null,
          cancelAtPeriodEnd: subscription.cancel_at_period_end || false,
          amount: null,
          currency: 'USD',
          paymentMethod: null,
        });
      }
    }
  }, [user, supabase]);

  // Fetch detailed subscription information
  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      if (!user || !session || !isSupabaseConfigured || !supabase) {
        setLoadingDetails(false);
        return;
      }

      try {
        setLoadingDetails(true);

        // Get subscription from stripe_subscriptions
        const { data: customer } = await supabase
          .from('stripe_customers')
          .select('customer_id')
          .eq('user_id', user.id)
          .is('deleted_at', null)
          .maybeSingle();

        if (customer) {
          const { data: subscription } = await supabase
            .from('stripe_subscriptions')
            .select('*')
            .eq('customer_id', customer.customer_id)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (subscription) {
            // ✅ current_period_end est déjà en TIMESTAMPTZ (ISO string) dans la DB
            // Pas besoin de multiplier par 1000
            setSubscriptionDetails({
              plan: subscription.metadata?.plan || plan,
              status: subscription.status,
              currentPeriodEnd: subscription.current_period_end || currentPeriodEnd,
              currentPeriodStart: subscription.current_period_start || null,
              cancelAtPeriodEnd: subscription.cancel_at_period_end || false,
              amount: subscription.amount ? subscription.amount / 100 : null,
              currency: subscription.currency || 'USD',
              paymentMethod: {
                brand: subscription.payment_method_brand || null,
                last4: subscription.payment_method_last4 || null,
                expMonth: subscription.payment_method_exp_month || null,
                expYear: subscription.payment_method_exp_year || null,
              }
            });
          }
        }
      } catch (error) {
        console.error('Error fetching subscription details:', error);
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchSubscriptionDetails();
  }, [user, session, plan, currentPeriodEnd]);

  // Fetch invoices from Stripe (via portal or direct API call)
  useEffect(() => {
    // Invoices are typically accessed via Stripe Portal
    // For now, we'll show a message to access them via portal
    setLoadingInvoices(false);
  }, []);

  const handleOpenPortal = async () => {
    setIsOpeningPortal(true);
    try {
      await openBillingPortal();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to open billing portal');
      setIsOpeningPortal(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscriptionDetails || subscriptionDetails.cancelAtPeriodEnd) {
      return;
    }

    setIsCancelling(true);
    try {
      await cancelSubscription();
      toast.success('Votre abonnement sera annulé à la fin de la période de facturation en cours.');
      setShowCancelConfirm(false);
      await refreshSubscription();
      
      // Refresh subscription details
      if (user && supabase) {
        const { data: customer } = await supabase
          .from('stripe_customers')
          .select('customer_id')
          .eq('user_id', user.id)
          .is('deleted_at', null)
          .maybeSingle();

        if (customer) {
          const { data: subscription } = await supabase
            .from('stripe_subscriptions')
            .select('*')
            .eq('customer_id', customer.customer_id)
            .is('deleted_at', null)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (subscription) {
            setSubscriptionDetails(prev => prev ? {
              ...prev,
              cancelAtPeriodEnd: subscription.cancel_at_period_end || false,
            } : null);
          }
        }
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to cancel subscription');
    } finally {
      setIsCancelling(false);
    }
  };

  const getPlanIcon = (planName: string | null) => {
    switch (planName?.toLowerCase()) {
      case 'scale':
        return <Crown className="w-5 h-5" />;
      case 'boost':
        return <Zap className="w-5 h-5" />;
      case 'lunch':
      default:
        return <Sparkles className="w-5 h-5" />;
    }
  };

  const getPlanColor = (planName: string | null) => {
    switch (planName?.toLowerCase()) {
      case 'scale':
        return 'from-purple-500 to-pink-500';
      case 'boost':
        return 'from-blue-500 to-cyan-500';
      case 'lunch':
      default:
        return 'from-gray-400 to-gray-600';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const getDaysUntilRenewal = (dateString: string | null): number | null => {
    if (!dateString) return null;
    try {
      const renewalDate = new Date(dateString);
      const now = new Date();
      const diffTime = renewalDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    } catch {
      return null;
    }
  };

  const getStatusBadge = () => {
    const currentStatus = subscriptionDetails?.status || status;
    if (!currentStatus) return null;
    
    const statusLower = currentStatus.toLowerCase();
    if (statusLower === 'active' || statusLower === 'trialing') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
          <CheckCircle className="w-4 h-4 mr-1" />
          {statusLower === 'trialing' ? 'En période d\'essai' : 'Actif'}
        </span>
      );
    } else if (statusLower === 'past_due' || statusLower === 'unpaid') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
          <XCircle className="w-4 h-4 mr-1" />
          Paiement en attente
        </span>
      );
    } else if (statusLower === 'canceled') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
          <XCircle className="w-4 h-4 mr-1" />
          Annulé
        </span>
      );
    }
    return null;
  };

  const currentPlan = subscriptionDetails?.plan || plan;
  const renewalDate = subscriptionDetails?.currentPeriodEnd || currentPeriodEnd;
  const daysUntilRenewal = getDaysUntilRenewal(renewalDate);

  if (isLoading || loadingDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[rgb(var(--brand))]" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold theme-text-primary mb-2">
          Abonnement et Paiement
        </h1>
        <p className="theme-text-secondary">
          Gérez votre abonnement, votre méthode de paiement et consultez votre historique de facturation.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Current Subscription Card */}
      <div className="theme-card-bg rounded-xl border border-[rgb(var(--border))] p-6 mb-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <h2 className="text-xl font-semibold theme-text-primary mb-3">
              Abonnement Actuel
            </h2>
            {currentPlan && (
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r ${getPlanColor(currentPlan)} text-white mb-4`}>
                {getPlanIcon(currentPlan)}
                <span className="font-semibold text-lg">{currentPlan}</span>
              </div>
            )}
            {subscriptionDetails?.amount && (
              <div className="flex items-center gap-2 text-sm theme-text-secondary mb-2">
                <DollarSign className="w-4 h-4" />
                <span className="font-semibold theme-text-primary">
                  {subscriptionDetails.amount.toFixed(2)} {subscriptionDetails.currency.toUpperCase()}
                </span>
                <span>/ mois</span>
              </div>
            )}
          </div>
          {getStatusBadge()}
        </div>

        {/* Renewal Information */}
        {renewalDate && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-[rgb(var(--card))] rounded-lg border border-[rgb(var(--border))]">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[rgb(var(--brand))] mt-0.5" />
              <div>
                <p className="text-sm font-medium theme-text-secondary mb-1">
                  {subscriptionDetails?.cancelAtPeriodEnd 
                    ? 'Expire le' 
                    : status?.toLowerCase() === 'trialing'
                    ? 'Fin de la période d\'essai'
                    : 'Prochain renouvellement'
                  }
                </p>
                <p className="text-lg font-semibold theme-text-primary">
                  {formatDate(renewalDate)}
                </p>
                {daysUntilRenewal !== null && daysUntilRenewal > 0 && (
                  <p className="text-xs theme-text-tertiary mt-1">
                    {daysUntilRenewal === 1 
                      ? 'Dans 1 jour' 
                      : `Dans ${daysUntilRenewal} jours`
                    }
                  </p>
                )}
              </div>
            </div>

            {subscriptionDetails?.currentPeriodStart && (
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[rgb(var(--brand))] mt-0.5" />
                <div>
                  <p className="text-sm font-medium theme-text-secondary mb-1">
                    Période actuelle
                  </p>
                  <p className="text-sm theme-text-primary">
                    {formatDate(subscriptionDetails.currentPeriodStart)} - {formatDate(renewalDate)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cancellation Warning */}
        {subscriptionDetails?.cancelAtPeriodEnd && (
          <div className="mb-6 p-4 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-500/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
                  Abonnement programmé pour annulation
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  Votre abonnement sera annulé le {formatDate(renewalDate)}. Vous continuerez à avoir accès jusqu'à cette date.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Payment Method */}
        {subscriptionDetails?.paymentMethod && (
          <div className="mb-6 p-4 bg-[rgb(var(--card))] rounded-lg border border-[rgb(var(--border))]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-[rgb(var(--brand))]" />
                <div>
                  <p className="text-sm font-medium theme-text-secondary mb-1">
                    Méthode de paiement
                  </p>
                  <div className="flex items-center gap-2">
                    {subscriptionDetails.paymentMethod.brand && (
                      <span className="text-xs uppercase font-semibold theme-text-secondary px-2 py-1 bg-[rgb(var(--card))] rounded border border-[rgb(var(--border))]">
                        {subscriptionDetails.paymentMethod.brand}
                      </span>
                    )}
                    <span className="text-sm font-mono theme-text-primary">
                      •••• •••• •••• {subscriptionDetails.paymentMethod.last4}
                    </span>
                    {subscriptionDetails.paymentMethod.expMonth && subscriptionDetails.paymentMethod.expYear && (
                      <span className="text-xs theme-text-tertiary">
                        Exp. {subscriptionDetails.paymentMethod.expMonth}/{subscriptionDetails.paymentMethod.expYear}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleOpenPortal}
            disabled={isOpeningPortal}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[rgb(var(--brand))] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isOpeningPortal ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Ouverture...
              </>
            ) : (
              <>
                <ExternalLink className="w-4 h-4" />
                Gérer dans Stripe
              </>
            )}
          </button>

          {!subscriptionDetails?.cancelAtPeriodEnd && currentPlan && currentPlan.toLowerCase() !== 'lunch' && (
            <>
              <button
                onClick={() => setShowCancelConfirm(true)}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 dark:bg-red-700 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-800 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Annuler l'abonnement
              </button>
              
              {/* Bouton Pause/Reprendre */}
              {subscriptionDetails?.cancelAtPeriodEnd ? (
                <button
                  onClick={async () => {
                    const result = await resumeSubscription();
                    if (result.success) {
                      await refreshSubscription();
                      await refreshSubscriptionDetails(); // ✅ Utilise la fonction helper
                    }
                  }}
                  disabled={pauseLoading}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 dark:bg-blue-700 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pauseLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Reprise...
                    </>
                  ) : (
                    <>
                      <Clock className="w-4 h-4" />
                      Reprendre l'abonnement
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={async () => {
                    const result = await pauseSubscription();
                    if (result.success) {
                      await refreshSubscription();
                      await refreshSubscriptionDetails(); // ✅ Utilise la fonction helper
                    }
                  }}
                  disabled={pauseLoading}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-yellow-600 dark:bg-yellow-700 text-white rounded-lg hover:bg-yellow-700 dark:hover:bg-yellow-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pauseLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Mise en pause...
                    </>
                  ) : (
                    <>
                      <Clock className="w-4 h-4" />
                      Mettre en pause
                    </>
                  )}
                </button>
              )}
            </>
          )}

          <button
            onClick={refreshSubscription}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 theme-card-bg border border-[rgb(var(--border))] rounded-lg hover:opacity-80 transition-opacity"
          >
            <RefreshCw className="w-4 h-4" />
            Actualiser
          </button>
        </div>

        <p className="mt-4 text-xs theme-text-tertiary">
          Le portail Stripe vous permet de changer de plan, mettre à jour votre carte de paiement, 
          consulter vos factures et gérer votre abonnement.
        </p>
      </div>

      {/* Cancel Confirmation Modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="theme-card-bg rounded-xl border border-[rgb(var(--border))] p-6 max-w-md w-full">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold theme-text-primary mb-2">
                  Annuler l'abonnement
                </h3>
                <p className="text-sm theme-text-secondary mb-4">
                  Êtes-vous sûr de vouloir annuler votre abonnement ? Vous continuerez à avoir accès jusqu'au {formatDate(renewalDate)}.
                </p>
              </div>
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="text-theme-text-secondary hover:text-theme-text-primary"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleCancelSubscription}
                disabled={isCancelling}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isCancelling ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                    Annulation...
                  </>
                ) : (
                  'Confirmer l\'annulation'
                )}
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                className="flex-1 px-4 py-2 theme-card-bg border border-[rgb(var(--border))] rounded-lg hover:opacity-80 transition-opacity"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Options */}
      {currentPlan && currentPlan.toLowerCase() !== 'scale' && (
        <div className="theme-card-bg rounded-xl border border-[rgb(var(--border))] p-6 mb-6">
          <h2 className="text-xl font-semibold theme-text-primary mb-4">
            Passer à un Plan Supérieur
          </h2>
          
          <div className="space-y-4">
            {currentPlan.toLowerCase() === 'lunch' && (
              <div className="flex items-center justify-between p-4 rounded-lg border border-[rgb(var(--border))] hover:border-[rgb(var(--brand))] transition-colors">
                <div className="flex items-center gap-3">
                  <Zap className="w-5 h-5 text-blue-500" />
                  <div>
                    <h3 className="font-semibold theme-text-primary">Boost</h3>
                    <p className="text-sm theme-text-secondary">
                      Accès au calendrier, workboard et support prioritaire
                    </p>
                  </div>
                </div>
                <SubscriptionButton
                  priceId={getPlanVariant('boost', 'month')?.priceId}
                  productId={getPlanVariant('boost', 'month')?.productId}
                  planName="Boost"
                  amount={getPlanVariant('boost', 'month')?.price || '$0'}
                  billingInterval="month"
                  trialDays={isTrialEligible ? 7 : undefined}
                  label={isTrialEligible ? 'Essai gratuit 7 jours' : 'S\'abonner à Boost'}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:opacity-90 transition-opacity"
                  onSuccess={() => {
                    toast.success('Abonnement Boost activé !');
                    refreshSubscription();
                  }}
                />
              </div>
            )}

            {currentPlan.toLowerCase() !== 'scale' && (
              <div className="flex items-center justify-between p-4 rounded-lg border border-[rgb(var(--border))] hover:border-[rgb(var(--brand))] transition-colors">
                <div className="flex items-center gap-3">
                  <Crown className="w-5 h-5 text-purple-500" />
                  <div>
                    <h3 className="font-semibold theme-text-primary">Scale</h3>
                    <p className="text-sm theme-text-secondary">
                      Statistiques avancées, factures et assistant AI
                    </p>
                  </div>
                </div>
                <SubscriptionButton
                  priceId={getPlanVariant('scale', 'month')?.priceId}
                  productId={getPlanVariant('scale', 'month')?.productId}
                  planName="Scale"
                  amount={getPlanVariant('scale', 'month')?.price || '$0'}
                  billingInterval="month"
                  label="S'abonner à Scale"
                  className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity"
                  onSuccess={() => {
                    toast.success('Abonnement Scale activé !');
                    refreshSubscription();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Billing History */}
      <div className="theme-card-bg rounded-xl border border-[rgb(var(--border))] p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold theme-text-primary">
            Historique de Facturation
          </h2>
          <button
            onClick={handleOpenPortal}
            className="text-sm text-[rgb(var(--brand))] hover:underline"
          >
            Voir toutes les factures
          </button>
        </div>
        
        <div className="p-8 text-center border border-[rgb(var(--border))] rounded-lg">
          <FileText className="w-12 h-12 mx-auto mb-3 theme-text-tertiary" />
          <p className="theme-text-secondary mb-2">
            Consultez toutes vos factures dans le portail Stripe
          </p>
          <button
            onClick={handleOpenPortal}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[rgb(var(--brand))] text-white rounded-lg hover:opacity-90 transition-opacity"
          >
            <ExternalLink className="w-4 h-4" />
            Accéder au portail Stripe
          </button>
        </div>
      </div>

      {/* Information Card */}
      <div className="theme-card-bg rounded-xl border border-[rgb(var(--border))] p-6">
        <div className="flex items-start gap-3 mb-4">
          <Info className="w-5 h-5 text-[rgb(var(--brand))] flex-shrink-0 mt-0.5" />
          <h2 className="text-xl font-semibold theme-text-primary">
            Informations Importantes
          </h2>
        </div>
        <ul className="space-y-3 text-sm theme-text-secondary">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
            <span>Les changements de plan sont appliqués immédiatement avec proration automatique.</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
            <span>L'annulation prend effet à la fin de la période de facturation en cours. Vous gardez l'accès jusqu'à cette date.</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
            <span>Vous pouvez mettre à jour votre méthode de paiement à tout moment via le portail Stripe.</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
            <span>Toutes vos factures sont disponibles dans le portail Stripe avec possibilité de téléchargement en PDF.</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PageBilling;

